import { Filter, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { apiRequest } from "../api";
import { DataTable, type Column } from "../components/DataTable";
import { DetailDrawer } from "../components/DetailDrawer";
import { PageHeader } from "../components/PageHeader";
import { Pagination } from "../components/Pagination";
import type { CollectionResponse, DataRecord } from "../types";

export interface FilterDefinition {
  key: string;
  label: string;
  placeholder?: string;
}

export interface CollectionConfig {
  title: string;
  description: string;
  endpoint: string;
  responseKey: string;
  columns: Column[];
  identityKeys: string[];
  filters?: FilterDefinition[];
}

export function CollectionPage({ config }: { config: CollectionConfig }) {
  const [page, setPage] = useState(1);
  const [draftFilters, setDraftFilters] = useState<Record<string, string>>({});
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [rows, setRows] = useState<DataRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<DataRecord | null>(null);
  const [refresh, setRefresh] = useState(0);
  const pageSize = 10;

  const query = useMemo(() => {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    for (const [key, value] of Object.entries(filters)) if (value.trim()) params.set(key, value.trim());
    return params.toString();
  }, [filters, page]);

  const load = useCallback(async (signal: AbortSignal) => {
    setLoading(true);
    setError("");
    try {
      const response = await apiRequest<CollectionResponse>(`${config.endpoint}?${query}`, { signal });
      const data = response[config.responseKey];
      setRows(Array.isArray(data) ? data.filter(isRecord) : []);
      setTotal(typeof response.total === "number" ? response.total : 0);
    } catch (failure) {
      if (signal.aborted) return;
      setError(failure instanceof Error ? failure.message : "Could not load records");
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [config.endpoint, config.responseKey, query]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load, refresh]);

  function applyFilters(event: React.FormEvent) {
    event.preventDefault();
    setPage(1);
    setFilters(draftFilters);
  }

  return (
    <>
      <PageHeader title={config.title} description={config.description} onRefresh={() => setRefresh((value) => value + 1)} refreshing={loading} />
      {config.filters?.length ? (
        <form className="filter-bar" onSubmit={applyFilters}>
          <Filter size={17} aria-hidden="true" />
          {config.filters.map((filter) => (
            <label key={filter.key}>
              <span>{filter.label}</span>
              <input value={draftFilters[filter.key] ?? ""} placeholder={filter.placeholder} onChange={(event) => setDraftFilters((current) => ({ ...current, [filter.key]: event.target.value }))} />
            </label>
          ))}
          <button className="secondary-button" type="submit"><Search size={16} />Apply</button>
        </form>
      ) : null}
      {error ? <div className="error-banner" role="alert">{error}</div> : null}
      <section className="data-surface" aria-busy={loading}>
        {loading && rows.length === 0 ? <div className="loading-state">Loading records…</div> : (
          <DataTable rows={rows} columns={config.columns} rowKey={(row, index) => config.identityKeys.map((key) => String(row[key] ?? "")).join(":") || String(index)} onInspect={setSelected} />
        )}
        <Pagination page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </section>
      <DetailDrawer title={config.title.replace(/s$/, "")} value={selected} onClose={() => setSelected(null)} />
    </>
  );
}

function isRecord(value: unknown): value is DataRecord {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}
